import { createColumnHelper } from '@tanstack/react-table';
import { Checkmark } from '@carbon/react/icons';
import { Link } from '@carbon/react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { blanksLast } from 'components/data-table/utils';
import { targetNpvColumn } from 'components/target-npv';
import type { PackageType } from 'pages/packages/list/types';
import {
  PACKAGE_TYPE_LABELS,
  formatInterestType,
} from 'pages/packages/list/utils';
import { SellersCell } from 'pages/packages/list/sellers-cell';
import type { TractDetailsRelatedPackage } from '../types';
import { useKeyedRows } from './hooks';
import {
  type DataTableRow,
  ACREAGE_DISPLAY_PRECISION,
  EMPTY_VALUE,
  STATIC_TABLE_CONFIG,
  renderLink,
} from './utils';
import styles from './related-packages-table.module.scss';

interface RelatedPackagesTableProps {
  rows: TractDetailsRelatedPackage[];
  isLoading: boolean;
}

type RelatedPackagesTableRow = DataTableRow<TractDetailsRelatedPackage>;

/**
 * Package ids are unique, so they key rows directly. Module scope so
 * `useKeyedRows`'s memo stays warm.
 *
 * @param {TractDetailsRelatedPackage} row - Source row
 * @param {number} idx - Position in the array
 * @return {string} Stable row key
 */
const getRowKey = (row: TractDetailsRelatedPackage, idx: number): string =>
  String(row.id ?? `row-${idx}`);

/**
 * Renders each acquirer entity as a link to its Insights record, stacked
 * vertically when a package has several. Mirrors the packages list page,
 * where `acquirerEntities` is likewise multi-valued.
 *
 * @param {TractDetailsRelatedPackage} pkg - The package row.
 * @return {React.ReactNode} Stacked entity links, or the empty fallback.
 */
const renderAcquirerEntities = (pkg: TractDetailsRelatedPackage) => {
  const entities = pkg.acquirerEntities ?? [];
  if (entities.length === 0) return EMPTY_VALUE;

  return (
    <div className={styles.stackedEntities}>
      {entities.map((entity) => (
        <Link
          key={entity.id}
          href={entity.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {entity.name ?? EMPTY_VALUE}
        </Link>
      ))}
    </div>
  );
};

const columnHelper = createColumnHelper<RelatedPackagesTableRow>();

const sortCol = (
  key: keyof RelatedPackagesTableRow & string,
  header: string,
  type: 'date' | 'integer'
): DataTableColumnDef<RelatedPackagesTableRow> => ({
  ...blanksLast<RelatedPackagesTableRow>(key),
  header,
  meta: { type },
});

// Exported so the spec can assert the column set (headers and meta types)
// directly against the AC without mounting the virtualized table.
export const COLUMNS: DataTableColumnDef<RelatedPackagesTableRow>[] = [
  columnHelper.display({
    id: 'name',
    header: 'Name',
    cell: ({ row }) =>
      renderLink(row.original.name ?? undefined, row.original.href),
  }),
  columnHelper.display({
    id: 'seller',
    header: 'Seller',
    // Each seller links to its Insights entity record when one is resolved,
    // matching the packages list page.
    cell: ({ row }) => (
      <SellersCell sellers={row.original.sellers ?? []} empty={EMPTY_VALUE} />
    ),
  }),
  columnHelper.display({
    id: 'acquirerEntities',
    header: 'Acquire Entity',
    cell: ({ row }) => renderAcquirerEntities(row.original),
  }),
  columnHelper.display({
    id: 'packageType',
    header: 'Package Type',
    // Insights-created packages have no STI type, so this cell is the
    // empty fallback for them by design.
    cell: ({ row }) => {
      const type = row.original.packageType;
      if (!type) return EMPTY_VALUE;
      return PACKAGE_TYPE_LABELS[type as PackageType] ?? type;
    },
  }),
  columnHelper.display({
    id: 'interestTypes',
    header: 'Interest Type',
    // `formatInterestType` is the packages list page's own formatter —
    // label lookup, title-case fallback for unknown types, `', '` join.
    cell: ({ row }) => {
      const types = row.original.interestTypes ?? [];
      if (types.length === 0) return EMPTY_VALUE;
      return formatInterestType(types.join(','));
    },
  }),
  sortCol('closeDate', 'Close Date', 'date'),
  {
    ...blanksLast<RelatedPackagesTableRow>('totalAcreage'),
    header: 'Total Acreage',
    // Only Opportunity packages carry a total acreage — see
    // Package#total_acreage. Account and Insights rows show the fallback.
    meta: {
      type: 'decimal',
      format: {
        minDecimalPlaces: ACREAGE_DISPLAY_PRECISION,
        maxDecimalPlaces: ACREAGE_DISPLAY_PRECISION,
      },
    },
  },
  {
    ...blanksLast<RelatedPackagesTableRow>('containsInventoryAssets'),
    header: 'Contains Inventory',
    cell: ({ getValue }) => {
      return getValue() ? <Checkmark size={20} /> : EMPTY_VALUE;
    },
  },
  sortCol('effectiveDate', 'Effective Date', 'date'),
  sortCol('completedEvals', 'Completed Evals', 'integer'),
  // The row is the package; its value opens the evaluation behind it.
  targetNpvColumn<RelatedPackagesTableRow>((row) => row.href),
  sortCol('workspaceCount', 'Workspaces', 'integer'),
  sortCol('createdAt', 'Created Date', 'date'),
];

/**
 * Static, read-only `<DataTable>` for the Summary step's "Related
 * Packages" section — every package containing this tract, across all
 * three package provenances (Opportunity and Account packages ingested
 * from Salesforce, plus packages assembled directly in Insights).
 * Columns mirror the packages list page.
 *
 * @param {RelatedPackagesTableProps} props - The component props.
 * @return {JSX.Element} The related packages table.
 */
export const RelatedPackagesTable = ({
  rows,
  isLoading,
}: RelatedPackagesTableProps): JSX.Element => {
  const keyedRows = useKeyedRows(rows, getRowKey);
  return (
    <DataTable<RelatedPackagesTableRow>
      data={keyedRows}
      columns={COLUMNS}
      getRowId={(row) => row.__key}
      isLoading={isLoading}
      config={{ ...STATIC_TABLE_CONFIG, sorting: true }}
    />
  );
};
